import { useState } from 'react'
import type { StateFinding, StateVersionSummary } from '@/lib/apiTypes'
import { useMediaQuery } from '@/lib/hooks'
import { ProvidersInUseTab } from '@/features/state/tabs/serials/ProvidersInUseTab'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { ResourceInspectorPanel } from '@/features/state/ResourceInspector'
import { FindingsList } from '@/features/state/tabs/serials/FindingsTab'
import { StatePolicyTab } from '@/features/state/tabs/serials/PolicyTab'
import { SerialPaneHeader } from '@/features/state/tabs/serials/SerialPaneHeader'
import { SerialRailRow } from '@/features/state/tabs/serials/SerialRailRow'
import { SERIAL_TABS, type SerialTab } from '@/features/state/tabs/serials/serialTabs'
import { Tabs } from '@/shared/components/Tabs'

/**
 * Serial rail + per-serial detail pane.
 *
 * The explicit height is load-bearing, not cosmetic: the modal body is `overflow-y: auto` inside a
 * `max-height: 90vh` panel, so without it the rail would scroll the whole modal instead of itself
 * and the pane header would leave the viewport. With it, rail and pane scroll independently.
 */
export function SerialsTab({
  orgId, workspace, versions, loading, selectedVersion, onSelect, onPromote,
  findings, findingsLoading, lockActive, allFindings, onMutated,
}: {
  orgId: string
  workspace: string
  versions: StateVersionSummary[]
  loading: boolean
  selectedVersion: StateVersionSummary | null
  onSelect: (v: StateVersionSummary) => void
  onPromote: (v: StateVersionSummary) => void
  findings: StateFinding[]
  findingsLoading: boolean
  lockActive: boolean
  allFindings: StateFinding[]
  onMutated: () => void
}) {
  const [tab, setTab] = useState<SerialTab>('findings')
  // Below this the rail cannot be a column and still leave the pane usable, so it becomes a
  // horizontal strip. Not a <select>: the findings dots are the reason to look at the list at all.
  const narrow = useMediaQuery('(max-width: 900px)')

  if (loading) return <SkeletonTable headers={['Serial', 'Detail']} rows={6} />
  if (versions.length === 0) return <EmptyState icon="◈" title="No state pushed yet." />

  const rail = (
    <div
      role="listbox"
      aria-label="State serials"
      style={narrow
        ? { display: 'flex', overflowX: 'auto', borderBottom: '1px solid var(--border)', flexShrink: 0 }
        : { width: 210, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--border)' }}
    >
      {versions.map((v, i) => (
        <div key={v.id} style={narrow ? { minWidth: 150, flexShrink: 0, borderRight: '1px solid var(--border)' } : undefined}>
          <SerialRailRow v={v} index={i} selected={selectedVersion?.id === v.id} onSelect={() => onSelect(v)} />
        </div>
      ))}
    </div>
  )

  return (
    <div style={{
      display: 'flex',
      flexDirection: narrow ? 'column' : 'row',
      height: 'min(64vh, 700px)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      {rail}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '12px 14px' }}>
        {!selectedVersion ? (
          <EmptyState icon="◈" title="Select a serial." subtitle="Pick a version from the list to inspect it." />
        ) : (
          <>
            <SerialPaneHeader
              v={selectedVersion}
              isLatest={versions[0]?.id === selectedVersion.id}
              onPromote={() => onPromote(selectedVersion)}
            />
            <Tabs
              tabs={SERIAL_TABS.map((t) => ({
                ...t,
                badge: t.key === 'findings' && findings.length > 0 ? (
                  <span className={`badge badge-${selectedVersion.critical_count > 0 ? 'red' : 'amber'}`} style={{ fontSize: '0.58rem' }}>
                    {findings.length}
                  </span>
                ) : undefined,
              }))}
              value={tab}
              onChange={setTab}
              label="Serial sections"
              style={{ marginBottom: 14 }}
            />

            {tab === 'findings' && <FindingsList findings={findings} loading={findingsLoading} />}

            {tab === 'resources' && (
              <ResourceInspectorPanel
                orgId={orgId}
                workspace={workspace}
                versions={versions}
                selectedVersionId={selectedVersion.id}
                lockActive={lockActive}
                findings={allFindings}
                onViewFindings={() => setTab('findings')}
                onMutated={onMutated}
              />
            )}

            {tab === 'policy' && (
              <StatePolicyTab orgId={orgId} workspace={workspace} versionId={selectedVersion.id} />
            )}

            {tab === 'providers' && (
              <ProvidersInUseTab orgId={orgId} workspace={workspace} versionId={selectedVersion.id} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

